import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { API } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { enterFullscreen } from "@/lib/fullscreen";
import { LangToggle } from "@/components/Toggles";

const inputCls = "w-full h-14 px-4 rounded-md bg-[#fffbe8] text-[#1a1420] text-center text-2xl font-bold outline-none border-4 border-[#1a1420] focus:border-[#FFD700] placeholder:text-slate-400";
const NAME_MAX = 20;

export default function PlayerJoin() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { t, setLang } = useLang();
  const [pin, setPin] = useState(sp.get("pin") || "");
  const [game, setGame] = useState(null);
  const [name, setName] = useState(() => {
    try {
      const last = JSON.parse(localStorage.getItem("sq_last_name") || "null");
      return last && last.name && Date.now() - last.ts < 24 * 60 * 60 * 1000 ? last.name : "";
    } catch { return ""; }
  });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const lookup = async (p) => {
    setLoading(true); setErr("");
    try {
      const { data } = await axios.get(`${API}/play/lookup/${p.trim()}`);
      setGame({ ...data, pin: p.trim() });
      setLang(data.language);
    } catch {
      setErr(t("game_not_found"));
    } finally { setLoading(false); }
  };

  useEffect(() => {
    const s = JSON.parse(localStorage.getItem("sq_player") || "null");
    const urlPin = sp.get("pin");
    if (s && (!urlPin || urlPin === s.pin)) {
      axios.get(`${API}/play/${s.game_id}/state`, { params: { player_id: s.player_id } })
        .then((r) => (r.data.phase !== "final" ? nav("/play") : localStorage.removeItem("sq_player")))
        .catch(() => localStorage.removeItem("sq_player"));
    }
    if (urlPin) lookup(urlPin);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const join = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    enterFullscreen();
    setLoading(true); setErr("");
    try {
      const { data } = await axios.post(`${API}/play/join`, { pin: game.pin, name: name.trim() });
      localStorage.setItem("sq_player", JSON.stringify({ ...data, pin: game.pin }));
      localStorage.setItem("sq_last_name", JSON.stringify({ name: name.trim(), ts: Date.now() }));
      nav("/play");
    } catch (e2) {
      setErr(e2.response?.status === 409 ? t("name_taken") : t("game_not_found"));
    } finally { setLoading(false); }
  };

  return (
    <div className="player-root items-center px-5 py-6">
      <div className="w-full flex justify-end"><LangToggle /></div>
      <div className="flex-1 w-full max-w-sm flex flex-col items-center justify-start pt-4 gap-6">
        <h1 className="font-press text-5xl text-[#FFD700] text-center" style={{ textShadow: "3px 3px 0 #000" }}>SamQuest</h1>
        {!game ? (
          <form className="w-full flex flex-col gap-3" onSubmit={(e) => { e.preventDefault(); pin.trim() && lookup(pin); }}>
            <label className="font-silk text-xl text-slate-300 text-center">{t("enter_pin")}</label>
            <input data-testid="player-join-pin-input" className={inputCls} inputMode="numeric" maxLength={6} autoFocus
              placeholder={t("pin_placeholder")} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} />
            <button data-testid="player-join-pin-submit-button" className="px-btn text-2xl mt-2" disabled={loading || pin.length < 6}>
              {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : t("enter")}
            </button>
          </form>
        ) : (
          <form className="w-full flex flex-col gap-3" onSubmit={join}>
            <div className="text-center font-pixel text-2xl text-slate-100" data-testid="player-join-game-title">{game.title}</div>
            <label className="font-silk text-xl text-slate-300 text-center">{t("your_name")}</label>
            <input data-testid="player-join-name-input" className={inputCls} maxLength={NAME_MAX} autoFocus
              placeholder={t("name_placeholder")} value={name} onChange={(e) => setName(e.target.value.slice(0, NAME_MAX))} />
            <div className="font-pixel text-base text-slate-400 text-center -mt-1" data-testid="player-join-name-count">{name.length}/{NAME_MAX}</div>
            <button data-testid="player-join-submit-button" className="px-btn text-2xl mt-2" disabled={loading || !name.trim()}>
              {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : t("join")}
            </button>
          </form>
        )}
        {err && <div data-testid="player-join-error" className="text-rose-300 font-pixel text-center text-lg">{err}</div>}
      </div>
    </div>
  );
}
